import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Save, Power, ExternalLink, Plus, Trash2, Leaf } from "lucide-react";

/**
 * Admin editor for per-client resident recycling guides.
 * Route: /app/guides
 */
export default function Guides() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [state, setState] = useState({ client: null, guide: null, exists: false });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => {
      // Only residential clients are eligible for a resident-facing guide
      const residential = r.data.filter((c) => c.is_residential);
      setClients(residential);
      if (residential.length && !clientId) setClientId(residential[0].id);
    });
  }, []);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/guides/by-client/${clientId}`);
      setState(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load guide");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clientId]);

  const enable = async () => {
    try {
      await api.post(`/guides/by-client/${clientId}/enable`);
      toast.success("Guide enabled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const disable = async () => {
    if (!window.confirm("Turn OFF the resident guide for this client? The public URL will stop working.")) return;
    try {
      await api.post(`/guides/by-client/${clientId}/disable`);
      toast.success("Guide disabled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/guides/by-client/${clientId}`, state.guide);
      toast.success("Guide saved");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const update = (path, val) => {
    setState((s) => {
      const g = JSON.parse(JSON.stringify(s.guide || {}));
      const parts = path.split(".");
      let cur = g;
      for (let i = 0; i < parts.length - 1; i++) {
        if (cur[parts[i]] == null) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = val;
      return { ...s, guide: g };
    });
  };

  const updateStream = (idx, key, val) => {
    setState((s) => {
      const g = { ...s.guide, streams: [...(s.guide.streams || [])] };
      g.streams[idx] = { ...g.streams[idx], [key]: val };
      return { ...s, guide: g };
    });
  };

  const updateStreamList = (idx, list, i, val) => {
    setState((s) => {
      const g = { ...s.guide, streams: [...(s.guide.streams || [])] };
      const arr = [...(g.streams[idx][list] || [])];
      arr[i] = val;
      g.streams[idx] = { ...g.streams[idx], [list]: arr };
      return { ...s, guide: g };
    });
  };
  const addStreamItem = (idx, list) => updateStreamList(idx, list, (state.guide.streams[idx][list] || []).length, "");
  const removeStreamItem = (idx, list, i) => {
    setState((s) => {
      const g = { ...s.guide, streams: [...(s.guide.streams || [])] };
      const arr = (g.streams[idx][list] || []).filter((_, j) => j !== i);
      g.streams[idx] = { ...g.streams[idx], [list]: arr };
      return { ...s, guide: g };
    });
  };

  const updateFaq = (i, key, val) => {
    setState((s) => {
      const faqs = [...(s.guide.faqs || [])];
      faqs[i] = { ...faqs[i], [key]: val };
      return { ...s, guide: { ...s.guide, faqs } };
    });
  };
  const addFaq = () => setState((s) => ({ ...s, guide: { ...s.guide, faqs: [...(s.guide.faqs || []), { question: "", answer: "" }] } }));
  const removeFaq = (i) => setState((s) => ({ ...s, guide: { ...s.guide, faqs: s.guide.faqs.filter((_, j) => j !== i) } }));

  const updateCode = (i, key, val) => {
    setState((s) => {
      const codes = [...(s.guide.plastic_codes || [])];
      codes[i] = { ...codes[i], [key]: key === "code" ? Number(val) : val };
      return { ...s, guide: { ...s.guide, plastic_codes: codes } };
    });
  };

  const resetCodesToDefault = () => {
    const defaults = [
      { code: 1, name: "PET / PETE", examples: "Water bottles, cold-drink bottles, cooking oil bottles", action: "recycle" },
      { code: 2, name: "HDPE", examples: "Milk jugs, detergent bottles, shampoo bottles, hard plastic containers", action: "recycle" },
      { code: 3, name: "PVC", examples: "Cling film, some pipes, blister packs, older kids' toys", action: "landfill" },
      { code: 4, name: "LDPE", examples: "Plastic bags, bread bags, squeezable bottles, cling film", action: "recycle" },
      { code: 5, name: "PP", examples: "Yogurt containers, margarine tubs, straws, bottle caps, takeaway containers", action: "recycle" },
      { code: 6, name: "PS (Polystyrene)", examples: "Foam takeaway boxes, egg cartons, cup lids, packaging peanuts", action: "landfill" },
      { code: 7, name: "Other", examples: "Mixed plastics, some baby bottles, lids on tubes", action: "check" },
    ];
    setState((s) => ({ ...s, guide: { ...s.guide, plastic_codes: defaults } }));
    toast.success("Codes 1–7 reset — remember to save.");
  };

  const enabled = state.client?.recycling_guide_enabled;
  const publicUrl = state.guide?.slug ? `${window.location.origin}/guide/${state.guide.slug}` : "";
  const g = state.guide;

  return (
    <div>
      <PageHeader
        eyebrow="Resident guides"
        title="Recycling guide"
        subtitle="Create a public, mobile-first guide residents can scan at their bin — no login required."
        actions={
          <div className="flex items-center gap-2">
            {enabled && publicUrl && (
              <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-2" data-testid="guide-open-public">
                <ExternalLink className="w-4 h-4" /> View public page
              </a>
            )}
            {enabled
              ? <button onClick={disable} className="btn-outline inline-flex items-center gap-2" data-testid="guide-disable-btn"><Power className="w-4 h-4" /> Turn off</button>
              : <button onClick={enable} className="btn-primary inline-flex items-center gap-2" data-testid="guide-enable-btn"><Power className="w-4 h-4" /> Turn on for this client</button>}
            {enabled && (
              <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="guide-save-btn">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
              </button>
            )}
          </div>
        }
      />

      <div className="px-8 lg:px-12 py-8 space-y-6">
        {/* Client picker */}
        {clients.length === 0 ? (
          <div className="hbt-card p-8 text-center" data-testid="guide-no-residential">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: "rgba(26,54,45,0.08)" }}>
              <Leaf className="w-6 h-6" style={{ color: "var(--forest)" }} />
            </div>
            <div className="font-display text-2xl font-semibold mt-4">No residential clients yet</div>
            <p className="text-neutral-500 text-sm mt-2 max-w-lg mx-auto">
              Resident recycling guides only apply to estates and residential complexes. Open <a href="/app/clients" className="underline" style={{ color: "var(--forest)" }}>Clients → Directory</a>, edit the client, and tick <b>&ldquo;Residential client&rdquo;</b>. They&apos;ll appear here right after.
            </p>
          </div>
        ) : (
        <div className="hbt-card p-5 flex items-center gap-3">
          <label className="hbt-label">Client</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="hbt-input max-w-[300px]" data-testid="guide-client-select">
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.recycling_guide_enabled ? " · guide on" : ""}</option>)}
          </select>
          {publicUrl && enabled && (
            <div className="ml-auto text-xs text-neutral-500 truncate max-w-[420px]" title={publicUrl}>
              Public URL: <span className="font-mono">{publicUrl}</span>
            </div>
          )}
        </div>
        )}

        {loading && <div className="text-center py-12 text-neutral-400">Loading…</div>}

        {!loading && g && !enabled && (
          <div className="hbt-card p-8 text-center" data-testid="guide-empty-state">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: "rgba(26,54,45,0.08)" }}>
              <Leaf className="w-6 h-6" style={{ color: "var(--forest)" }} />
            </div>
            <div className="font-display text-2xl font-semibold mt-4">Turn on the resident guide</div>
            <p className="text-neutral-500 text-sm mt-2 max-w-md mx-auto">
              This creates a public URL residents can visit (or scan from a QR on their bin) to learn what goes in the green vs black bag. A friendly starter guide gets pre-filled — you can edit everything before sharing.
            </p>
            <button onClick={enable} className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="guide-enable-first-btn">
              <Power className="w-4 h-4" /> Turn on for this client
            </button>
          </div>
        )}

        {!loading && g && enabled && (
          <>
            {/* Hero */}
            <div className="hbt-card p-6 space-y-4" data-testid="guide-editor-hero">
              <div className="hbt-label">Hero (top of page)</div>
              <div>
                <label className="text-xs text-neutral-500 block mb-1">Estate name (shown in title)</label>
                <input value={g.estate_name || ""} onChange={(e) => update("estate_name", e.target.value)} className="hbt-input" data-testid="guide-estate-name" />
              </div>
              <div>
                <label className="text-xs text-neutral-500 block mb-1">Public URL slug</label>
                <input value={g.slug || ""} onChange={(e) => update("slug", e.target.value)} className="hbt-input font-mono text-sm" data-testid="guide-slug" />
              </div>
              <div>
                <label className="text-xs text-neutral-500 block mb-1">Big headline</label>
                <input value={g.hero_title || ""} onChange={(e) => update("hero_title", e.target.value)} className="hbt-input" data-testid="guide-hero-title" />
              </div>
              <div>
                <label className="text-xs text-neutral-500 block mb-1">One-line message under the headline</label>
                <input value={g.hero_stat || ""} onChange={(e) => update("hero_stat", e.target.value)} className="hbt-input" data-testid="guide-hero-stat" />
              </div>
            </div>

            {/* Streams */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="guide-editor-streams">
              {(g.streams || []).map((s, idx) => (
                <div key={idx} className="hbt-card overflow-hidden" data-testid={`stream-editor-${s.key}`}>
                  <div className="p-4 text-white flex items-center gap-3" style={{ background: s.color || "#4A4D4A" }}>
                    <input value={s.label || ""} onChange={(e) => updateStream(idx, "label", e.target.value)} className="bg-transparent border-b border-white/30 focus:outline-none flex-1 text-sm font-semibold" />
                    <input value={s.color || "#4A4D4A"} onChange={(e) => updateStream(idx, "color", e.target.value)} className="bg-transparent w-16 text-xs font-mono border-b border-white/30 focus:outline-none" />
                  </div>
                  <div className="p-5 space-y-4">
                    {["accepted", "rejected"].map((list) => (
                      <div key={list}>
                        <div className="hbt-label mb-2 flex items-center justify-between">
                          <span>{list === "accepted" ? "Yes, put in" : "No, keep out"}</span>
                          <button onClick={() => addStreamItem(idx, list)} className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
                        </div>
                        <div className="space-y-1.5">
                          {(s[list] || []).map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <input value={item} onChange={(e) => updateStreamList(idx, list, i, e.target.value)} className="hbt-input text-sm flex-1" placeholder="e.g. Plastic bottles" />
                              <button onClick={() => removeStreamItem(idx, list, i)} className="text-neutral-400 hover:text-[var(--terracotta,#C85A3C)] p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Plastic codes */}
            <div className="hbt-card p-6 space-y-3" data-testid="guide-editor-codes">
              <div className="hbt-label">Plastic codes 1-7</div>
              <p className="text-xs text-neutral-500">Set which action each recycling code triggers. Local recycling facilities differ — HBT in Eswatini accepts 1, 2, 4 and 5 as recyclable.</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                {(g.plastic_codes || []).map((c, i) => (
                  <div key={i} className="border rounded-md p-3 flex items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid={`code-editor-${c.code}`}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm" style={{ background: "rgba(26,54,45,0.08)", color: "var(--forest)" }}>{c.code}</div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input value={c.name} onChange={(e) => updateCode(i, "name", e.target.value)} className="hbt-input text-xs" placeholder="Code name" />
                      <select value={c.action} onChange={(e) => updateCode(i, "action", e.target.value)} className="hbt-input text-xs" data-testid={`code-action-${c.code}`}>
                        <option value="recycle">Green bag</option>
                        <option value="landfill">Black bag</option>
                        <option value="check">Ask us / unsure</option>
                      </select>
                      <input value={c.examples} onChange={(e) => updateCode(i, "examples", e.target.value)} className="hbt-input text-xs sm:col-span-2" placeholder="Common examples residents see" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bag supply */}
            <div className="hbt-card p-6 space-y-4" data-testid="guide-editor-supply">
              <div className="hbt-label">Where to get green bags</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input placeholder="Location" value={g.bag_supply?.location || ""} onChange={(e) => update("bag_supply.location", e.target.value)} className="hbt-input" />
                <input placeholder="Hours" value={g.bag_supply?.hours || ""} onChange={(e) => update("bag_supply.hours", e.target.value)} className="hbt-input" />
                <input placeholder="Price" value={g.bag_supply?.price || ""} onChange={(e) => update("bag_supply.price", e.target.value)} className="hbt-input" />
              </div>
              <input placeholder="Extra note (optional)" value={g.bag_supply?.notes || ""} onChange={(e) => update("bag_supply.notes", e.target.value)} className="hbt-input" />
            </div>

            {/* Collection days */}
            <div className="hbt-card p-6 space-y-2" data-testid="guide-editor-days">
              <div className="hbt-label">Collection days</div>
              <textarea rows={2} value={g.collection_days || ""} onChange={(e) => update("collection_days", e.target.value)} className="hbt-input" />
            </div>

            {/* Bulky items */}
            <div className="hbt-card p-6 space-y-2" data-testid="guide-editor-bulky">
              <div className="hbt-label">Big / bulky items</div>
              <p className="text-xs text-neutral-500">Explain to residents what to do with items too big for a bag (large boxes, packaging etc.).</p>
              <textarea rows={2} value={g.bulky_items || ""} onChange={(e) => update("bulky_items", e.target.value)} className="hbt-input" placeholder="Large boxes can be left flat next to the collection point on collection day…" />
            </div>

            {/* FAQs */}
            <div className="hbt-card p-6 space-y-3" data-testid="guide-editor-faqs">
              <div className="flex items-center justify-between">
                <div className="hbt-label">Frequently asked</div>
                <button onClick={addFaq} className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add question</button>
              </div>
              {(g.faqs || []).map((f, i) => (
                <div key={i} className="border rounded-md p-3 space-y-2" style={{ borderColor: "var(--border)" }}>
                  <div className="flex items-center gap-2">
                    <input placeholder="Question" value={f.question} onChange={(e) => updateFaq(i, "question", e.target.value)} className="hbt-input flex-1 font-medium" />
                    <button onClick={() => removeFaq(i)} className="text-neutral-400 hover:text-[var(--terracotta,#C85A3C)] p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <textarea rows={2} placeholder="Answer" value={f.answer} onChange={(e) => updateFaq(i, "answer", e.target.value)} className="hbt-input" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
